import { FormattedMessage } from 'react-intl';

import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { FILTERS } from '@sorare/core/src/lib/filters';
import { cardAttributes, glossary } from '@sorare/core/src/lib/glossary';

import { widget as ShowDetails } from '#src/components/TokenPreview/ShowDetails/index.tsx';
import { RangeSlider as ConnectedRangeSlider } from '#src/search/RangeSlider/index.tsx';
import { BackendDuplicateFilter } from '#src/searchCards/DuplicateFilter/index.tsx';
import { makeRangeFilter } from '#src/searchCards/RangeSlider/index.tsx';
import { RefineBackendCardPlayerAge } from '#src/searchCards/RefineAge/index.tsx';
import { RefineBackendAppearances } from '#src/searchCards/RefineAppearances/index.tsx';
import { RefineBackendAverageScore } from '#src/searchCards/RefineAverageScore/index.tsx';
import {
  RefineActiveClub,
  RefineBackendActiveClub,
  RefineBackendClub,
  RefineClub,
} from '#src/searchCards/RefineClubs/index.tsx';
import {
  RefineBackendBirthplace,
  RefineBackendCardEdition,
  RefineBackendPlayer,
  RefineBackendSeason,
  RefineSingleYearSeason,
} from '#src/searchCards/RefineList/index.tsx';
import {
  RefineClassicProjection,
  RefineDailyProjection,
} from '#src/searchCards/RefineProjection/index.tsx';
import { backendWidget as BackendRefineSkin } from '#src/searchCards/RefineSkin/index.tsx';
import {
  AcceptedCurrencyGroup,
  BackendAcceptedCurrencyGroup,
  BackendBidToEarnFilter,
  BackendDailyFilter,
  BackendNotInLineupFilter,
  BackendPriceGroup,
  BackendPromotionalEventFilter,
  BackendRarityGroup,
  BackendRefineCreditEligible,
  BackendRefineInSeason,
  BackendRefineOnSale,
  BackendWatchlistGroup,
  BidToEarnFilter,
  CollectibleGroup,
  DailyFilter,
  NotInLineupFilter,
  PriceGroup,
  PromotionalEventFilter,
  PromotionalEventFilterForInstantBuy,
  RarityGroup,
  RefineAppearances,
  RefineBirthplace,
  RefineBackendCardLevel,
  RefineBackendPosition,
  RefineBackendSerialNumber,
  RefineCardEdition,
  RefineCardLevel,
  RefineCardPlayerAge,
  RefineCreditEligible,
  RefineInSeason,
  RefineOnSale,
  RefinePlayer,
  RefinePosition,
  RefineSerialNumber,
  filterSeparator,
} from '#src/searchCards/index.ts';
import { WatchlistGroup } from '#src/searchCards/refineGroups.tsx';

import { backendWidget as BackendNbaCustomDecksFilter } from './NbaCustomDecksFilter/BackendNbaCustomDecksFilter.tsx';
import { widget as NbaCustomDecksFilter } from './NbaCustomDecksFilter/index.tsx';

const MAX_TEAM_LIMIT = 30;

export const RefineTenGameAverageScore = makeRangeFilter(({ visible }) => {
  return (
    <ConnectedRangeSlider
      attribute={FILTERS.lastTenPlayedAverageScore.attribute}
      visible={visible}
    />
  );
}, FILTERS.lastTenPlayedAverageScore);

const CardGroup = {
  slug: 'card',
  title: <FormattedMessage {...glossary.card} />,
  filters: [RefineSingleYearSeason, RefineClub(MAX_TEAM_LIMIT)],
};
export const MatchInfoGroup = {
  slug: 'match-info',
  title: <FormattedMessage {...glossary.matchInfo} />,
  filters: [DailyFilter, RefineTenGameAverageScore, RefineAppearances()],
};

export const PlayerGroup = (props?: {
  withoutActiveClub?: boolean;
  withClub?: boolean;
}) => ({
  slug: 'player',
  title: <FormattedMessage {...cardAttributes.player} />,
  filters: [
    RefinePosition(),
    RefineCardPlayerAge,
    RefineBirthplace,
    RefinePlayer,
    ...((props?.withoutActiveClub && []) || [RefineActiveClub(MAX_TEAM_LIMIT)]),
    ...((props?.withClub && [RefineClub(MAX_TEAM_LIMIT)]) || []),
  ],
});

export const ProjectionGroup = () => ({
  slug: 'projection',
  title: <FormattedMessage {...glossary.projection} />,
  filters: [RefineDailyProjection, RefineClassicProjection],
});

const CollectiblesGroup = (props?: { withoutCardLevel?: boolean }) => ({
  slug: 'collectibles',
  title: <FormattedMessage {...cardAttributes.collectibles} />,
  filters: [
    RefineSerialNumber,
    ...((props?.withoutCardLevel && []) || [RefineCardLevel]),
    RefineCardEdition,
  ],
});

const ToggleGroup = (filters: FilterWidget[]) => ({
  slug: 'toggle',
  title: null,
  filters: [
    ...(filters.flatMap((filter, i) => [
      ...((i && [filterSeparator]) || []),
      filter,
    ]) || []),
  ],
});

const InSeasonFilter = {
  slug: 'inSeason',
  title: null,
  filter: RefineInSeason,
  filters: [],
};

export const primaryMarketFilters = [
  RarityGroup,
  {
    slug: 'toggle',
    title: null,
    filters: [PromotionalEventFilter],
  },
  PriceGroup,
  ToggleGroup([BidToEarnFilter()]),
  WatchlistGroup,
  CardGroup,
  MatchInfoGroup,
  PlayerGroup(),
  CollectiblesGroup({ withoutCardLevel: true }),
];

export const instantBuyMarketFilters = [
  RarityGroup,
  {
    slug: 'toggle',
    title: null,
    filters: [PromotionalEventFilterForInstantBuy],
  },
  PriceGroup,
  WatchlistGroup,
  CardGroup,
  MatchInfoGroup,
  PlayerGroup(),
  CollectiblesGroup({ withoutCardLevel: true }),
];

export const secondaryMarketFilters = [
  InSeasonFilter,
  RarityGroup,
  PriceGroup,
  WatchlistGroup,
  CardGroup,
  MatchInfoGroup,
  PlayerGroup(),
  CollectiblesGroup(),
  AcceptedCurrencyGroup,
].filter(Boolean);

export const secondaryMarketStackedFilters = [
  PriceGroup,
  CollectiblesGroup(),
  AcceptedCurrencyGroup,
].filter(Boolean);

export const playerPageFilters = [
  InSeasonFilter,
  ToggleGroup([RefineOnSale(), RefineCreditEligible()]),
  RarityGroup,
  PriceGroup,
  CardGroup,
  MatchInfoGroup,
  CollectiblesGroup(),
  AcceptedCurrencyGroup,
];

export const clubPageFilters = [
  InSeasonFilter,
  ToggleGroup([RefineOnSale(), RefineCreditEligible()]),
  RarityGroup,
  PriceGroup,
  WatchlistGroup,
  CardGroup,
  MatchInfoGroup,
  PlayerGroup({ withoutActiveClub: false }),
  CollectiblesGroup(),
  AcceptedCurrencyGroup,
];

export const tradeFilters = [
  InSeasonFilter,
  RarityGroup,
  { slug: 'not-in-lineup', filters: [NotInLineupFilter] },
  WatchlistGroup,
  { slug: 'deck', filters: [NbaCustomDecksFilter] },
  CardGroup,
];

const BackendInSeasonFilter = {
  slug: 'inSeason',
  title: null,
  filter: BackendRefineInSeason,
  filters: [],
};

const BackendCardGroup = {
  slug: 'card',
  title: <FormattedMessage {...glossary.card} />,
  filters: [RefineBackendSeason, RefineBackendClub(MAX_TEAM_LIMIT)],
};

const BackendMatchInfoGroup = {
  slug: 'match-info',
  title: <FormattedMessage {...glossary.matchInfo} />,
  filters: [
    BackendDailyFilter,
    RefineBackendAverageScore({ disableFiveAverageScore: true }),
    RefineBackendAppearances(),
  ],
};

const BackendPlayerGroup = () => ({
  slug: 'player',
  title: <FormattedMessage {...cardAttributes.player} />,
  filters: [
    RefineBackendPosition(),
    RefineBackendCardPlayerAge,
    RefineBackendBirthplace,
    RefineBackendPlayer,
    RefineBackendActiveClub(MAX_TEAM_LIMIT),
  ],
});

export const backendPrimaryMarketFilters = () => [
  BackendRarityGroup,
  {
    slug: 'PromotionalEvent',
    title: null,
    filters: [BackendPromotionalEventFilter],
  },
  BackendPriceGroup,
  ToggleGroup([BackendBidToEarnFilter()]),
  BackendWatchlistGroup,
  BackendCardGroup,
  BackendMatchInfoGroup,
  BackendPlayerGroup(),
  CollectibleGroup([RefineBackendSerialNumber, RefineBackendCardEdition]),
];

const BackendCollectiblesGroup = () =>
  CollectibleGroup([
    RefineBackendSerialNumber,
    RefineBackendCardLevel,
    RefineBackendCardEdition,
  ]);

export const backendSecondaryMarketFilters = () => [
  BackendInSeasonFilter,
  BackendRarityGroup,
  BackendPriceGroup,
  BackendWatchlistGroup,
  BackendCardGroup,
  BackendMatchInfoGroup,
  BackendPlayerGroup(),
  BackendCollectiblesGroup(),
  BackendAcceptedCurrencyGroup,
];

export const backendSecondaryMarketStackedFilters = () => [
  BackendInSeasonFilter,
  BackendPriceGroup,
  BackendCollectiblesGroup(),
  BackendAcceptedCurrencyGroup,
];

export const backendPlayerPageFilters = () => [
  BackendInSeasonFilter,
  ToggleGroup([BackendRefineOnSale(), BackendRefineCreditEligible()]),
  BackendRarityGroup,
  BackendPriceGroup,
  BackendCardGroup,
  BackendMatchInfoGroup,
  BackendCollectiblesGroup(),
  BackendAcceptedCurrencyGroup,
];

export const backendClubPageFilters = () => [
  BackendInSeasonFilter,
  ToggleGroup([BackendRefineOnSale(), BackendRefineCreditEligible()]),
  BackendRarityGroup,
  BackendPriceGroup,
  BackendWatchlistGroup,
  BackendCardGroup,
  BackendMatchInfoGroup,
  BackendPlayerGroup(),
  BackendCollectiblesGroup(),
  BackendAcceptedCurrencyGroup,
];

export const backendTradeFilters = () => [
  BackendInSeasonFilter,
  BackendRarityGroup,
  { slug: 'not-in-lineup', filters: [BackendNotInLineupFilter] },
  BackendWatchlistGroup,
  { slug: 'deck', filters: [BackendNbaCustomDecksFilter] },
  BackendCardGroup,
];

export const backendMyNBAClubFilters = ({
  showDecksFilter = true,
}: {
  showDecksFilter?: boolean;
} = {}) =>
  [
    { slug: 'show-details', filters: [ShowDetails] },
    BackendInSeasonFilter,
    ToggleGroup([BackendRefineOnSale(), BackendNotInLineupFilter]),
    ...((showDecksFilter && [
      { slug: 'decks', filters: [BackendNbaCustomDecksFilter] },
    ]) ||
      []),
    BackendCardGroup,
    BackendMatchInfoGroup,
    BackendPlayerGroup(),
    CollectibleGroup([
      RefineBackendSerialNumber,
      RefineBackendCardLevel,
      RefineBackendCardEdition,
    ]),
    BackendAcceptedCurrencyGroup,
  ].filter(Boolean);

export const backendMyNBACommonCardsFilters = ({
  showDecksFilter = true,
  setSlug,
}: {
  showDecksFilter?: boolean;
  setSlug: Nullable<string>;
}) =>
  [
    {
      slug: 'skin',
      title: null,
      filters: [BackendRefineSkin(setSlug)],
    },
    {
      slug: 'duplicatedKey',
      filters: [BackendDuplicateFilter],
    },
    ...((showDecksFilter && [
      { slug: 'decks', filters: [BackendNbaCustomDecksFilter] },
    ]) ||
      []),
    {
      slug: 'card',
      title: <FormattedMessage {...glossary.card} />,
      filters: [RefineBackendClub(MAX_TEAM_LIMIT)],
    },
    BackendMatchInfoGroup,
  ].filter(Boolean);
